import { useId, useMemo } from "react"

import classNames from "classnames"

import { toggle } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useRovingTabIndex } from "shared/lib/useRovingTabIndex"
import { Direction } from "shared/types/Direction"
import { IconSize } from "shared/types/Icon"
import { Ref } from "shared/types/Ref"
import { RovingTabIndexItem } from "shared/types/RovingTabIndex"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { useFirstSelectedOption } from "shared/ui/input/select/hooks/useFirstSelectedOption"
import { useIsOptionDisabled } from "shared/ui/input/select/hooks/useIsOptionDisabled"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { BoxSelectLoader } from "./BoxSelectLoader"
import { BoxSelectOption, BoxSelectOptionProps } from "./BoxSelectOption"

interface BoxSelectOptionWithRefProps<T>
  extends Omit<BoxSelectOptionProps<T>, "isSelected" | "tabIndex"> {
  index: number
  isMulti: boolean
  value: T[] | T | null | undefined
  elementProps: RovingTabIndexItem<HTMLButtonElement>
}

function BoxSelectOptionWithRef<T>({
  index,
  isMulti,
  value,
  elementProps,
  ...props
}: BoxSelectOptionWithRefProps<T>) {
  const { tabIndex, onFocus } = elementProps
  const isSelected = useMemo(
    () =>
      Array.isArray(value)
        ? value.includes(props.option.value as T)
        : value === props.option.value,
    [value, props.option.value]
  )
  return (
    <BoxSelectOption
      {...props}
      isSelected={isSelected}
      tabIndex={tabIndex}
      onFocus={onFocus}
      inputRef={elementProps.ref}
    />
  )
}

export interface BoxSelectProps<T, M extends boolean = false>
  extends WithClassName {
  id?: string
  isMulti?: M
  label?: string
  value?: (M extends true ? T[] : T) | null | undefined
  options: SelectOption<T>[] | null | undefined
  disabledOptions?: T[] | null
  onChange?: (value: (M extends true ? T[] : T) | null) => void
  isDisabled?: boolean
  isLoading?: boolean
  isReadonly?: boolean
  inputRef?: Ref<HTMLButtonElement>
  iconSize?: IconSize
  isIconOnly?: boolean
  optionClassName?: string
  useValueAsLabel?: boolean
  ariaDescribedBy?: string
}

export function BoxSelectControl<T, M extends boolean>({
  id,
  label,
  options,
  value,
  isLoading,
  isDisabled,
  isReadonly,
  disabledOptions,
  className,
  optionClassName,
  isMulti = false as M,
  inputRef,
  ariaDescribedBy,
  onChange,
  ...rest
}: BoxSelectProps<T, M>) {
  const internalID = useId()
  const isOptionDisabled = useIsOptionDisabled(disabledOptions)
  const firstSelectedOptionIndex = useFirstSelectedOption({
    options,
    selectedOptions: value
  })

  const { containerProps, getElementProps } = useRovingTabIndex<
    HTMLButtonElement,
    HTMLDivElement
  >({
    direction: Direction.HORIZONTAL,
    elementCount: options?.length ?? 0,
    firstSelectedElementIndex: Math.max(0, firstSelectedOptionIndex ?? 0),
    disableRovingTabIndex: isMulti,
    ref: inputRef
  })

  const onSelect = useEvent((selectedValue: T) => {
    if (!isMulti) {
      onChange?.(selectedValue as (M extends true ? T[] : T) | null)
    } else {
      onChange?.(
        toggle(selectedValue, (value as T[]) || []) as
          | (M extends true ? T[] : T)
          | null
      )
    }
  })

  if (isLoading) {
    return (
      <BoxSelectLoader
        className={className}
        optionClassName={optionClassName}
        data-testid="box-select-list__loading"
      />
    )
  }

  return (
    <div
      id={id}
      role={isMulti ? undefined : "toolbar"}
      aria-label={label}
      aria-describedby={ariaDescribedBy}
      className={classNames("box-select", { disabled: isDisabled }, className)}
      {...containerProps}
      onKeyDown={isMulti ? undefined : containerProps.onKeyDown}
    >
      <Tooltip.Provider>
        {options?.map((option, index) => (
          <BoxSelectOptionWithRef
            key={typeof option.value === "string" ? option.value : index}
            index={index}
            isMulti={isMulti}
            value={value as T[] | T | null | undefined}
            elementProps={getElementProps(index)}
            type={isMulti ? "checkbox" : "radio"}
            name={id || internalID}
            option={option}
            isDisabled={isDisabled || isOptionDisabled(option)}
            onChange={onSelect}
            iconSize={rest.iconSize}
            isIconOnly={rest.isIconOnly}
            useValueAsLabel={rest.useValueAsLabel}
            optionClassName={optionClassName}
          />
        ))}
      </Tooltip.Provider>
    </div>
  )
}
